import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Small dropdown menu: a trigger button and a panel that closes on outside-click, Escape, or
 * choosing an item.
 *
 * The panel is rendered into `document.body` through a portal and positioned `fixed`. That's not
 * incidental — the deal header it's used in is `overflow-hidden` (to keep the gradient inside its
 * rounded corners), which would otherwise clip the menu to a sliver at the header's bottom edge.
 * A portal makes the component safe to drop inside any clipping ancestor without the caller having
 * to know or care.
 *
 * `trigger` is a render prop so the caller can reflect open state (rotate a caret, hold a pressed
 * style) without this component prescribing how the button looks.
 */
export function Menu({
  trigger,
  children,
  align = 'right',
  panelClass = '',
  label = 'Open menu',
}: {
  trigger: (open: boolean) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right';
  panelClass?: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left?: number; right?: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const r = btn.current?.getBoundingClientRect();
    if (!r) return;
    const GAP = 6;
    const panelH = panel.current?.offsetHeight ?? 0;
    // Flip above the trigger when there isn't room below (a deal near the bottom of a short
    // viewport), so the last items stay reachable.
    const below = window.innerHeight - r.bottom;
    const top = panelH && below < panelH + GAP && r.top > panelH + GAP
      ? r.top - panelH - GAP
      : r.bottom + GAP;
    setPos(align === 'right'
      ? { top, right: Math.max(8, window.innerWidth - r.right) }
      : { top, left: Math.max(8, r.left) });
  }, [align]);

  // Measure before paint so the panel never renders at the wrong spot for a frame.
  useLayoutEffect(() => { if (open) place(); }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btn.current?.contains(t) && !panel.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onMove = () => place();
    // `mousedown`, not `click`: closing on mousedown means a click that starts inside the panel
    // and drifts outside (text selection) doesn't leave a stuck-open menu.
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    // `true` — catch scrolls on any ancestor, not just the window, so the panel tracks the trigger.
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  return (
    <>
      <button
        ref={btn}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
      >
        {trigger(open)}
      </button>
      {open && createPortal(
        <div
          ref={panel}
          role="menu"
          style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left, right: pos?.right }}
          className={`z-[60] min-w-[210px] max-h-[70vh] overflow-y-auto rounded-xl border border-line
            bg-card shadow-sh2 py-1.5 ${panelClass}`}
        >
          {children(() => setOpen(false))}
        </div>,
        document.body,
      )}
    </>
  );
}

/** A row in a {@link Menu}. `tone="danger"` marks the destructive one. */
export function MenuItem({
  icon,
  children,
  onClick,
  disabled = false,
  tone = 'default',
  note,
  trailing,
}: {
  icon?: ReactNode;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
  note?: ReactNode;
  trailing?: ReactNode;
}) {
  const danger = tone === 'danger';
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={`w-full text-left px-3 py-1.5 text-[12.5px] flex items-center gap-2.5
        disabled:opacity-40 disabled:cursor-not-allowed
        ${danger ? 'text-red hover:bg-red-50' : 'text-navy hover:bg-canvas'}`}
    >
      {icon && <span className="w-[15px] text-center flex-none opacity-80">{icon}</span>}
      <span className="flex-1 min-w-0">
        <span className="font-semibold">{children}</span>
        {note && <span className="block text-[10.5px] text-muted-2 font-normal leading-tight mt-0.5">{note}</span>}
      </span>
      {trailing && <span className="flex-none text-[10px] text-muted-2 uppercase tracking-[0.4px] font-bold">{trailing}</span>}
    </button>
  );
}

/** Group heading inside a menu ("PIPELINE", "CLOSED"). */
export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-3 pt-2 pb-1 text-[9.5px] font-bold uppercase tracking-[1px] text-muted-2">{children}</div>
  );
}

export function MenuSeparator() {
  return <div className="my-1.5 border-t border-line-2" />;
}
